import { useCallback, useEffect, useRef, useState, type ChangeEvent, type CompositionEvent } from "react";
import { useSearchParams } from "react-router-dom";

export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export type ListPageSize = (typeof PAGE_SIZE_OPTIONS)[number];
export type SortDir = "asc" | "desc";

function positiveInteger(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * URL-backed search + pagination for list pages (`q`, `page`, `pageSize`).
 * Extra filters stay in the same query string via `updateParam`.
 */
export function useListQueryState() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const page = positiveInteger(searchParams.get("page"), 1);
  const requestedPageSize = positiveInteger(searchParams.get("pageSize"), PAGE_SIZE_OPTIONS[0]);
  const pageSize = PAGE_SIZE_OPTIONS.includes(requestedPageSize as ListPageSize)
    ? requestedPageSize
    : PAGE_SIZE_OPTIONS[0];

  const patchParams = useCallback(
    (mutate: (next: URLSearchParams) => void, replace = false) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams],
  );

  const updatePage = useCallback(
    (nextPage: number) => {
      patchParams((next) => {
        if (nextPage <= 1) next.delete("page");
        else next.set("page", String(nextPage));
      });
    },
    [patchParams],
  );

  const updatePageSize = useCallback(
    (nextPageSize: number) => {
      patchParams((next) => {
        next.delete("page");
        if (nextPageSize === PAGE_SIZE_OPTIONS[0]) next.delete("pageSize");
        else next.set("pageSize", String(nextPageSize));
      });
    },
    [patchParams],
  );

  const updateSearch = useCallback(
    (value: string) => {
      patchParams((next) => {
        next.delete("page");
        if (value) next.set("q", value);
        else next.delete("q");
      }, true);
    },
    [patchParams],
  );

  const searchInput = useSearchDraft(q, updateSearch);

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      patchParams((next) => {
        next.delete("page");
        if (value == null || value === "") next.delete(key);
        else next.set(key, value);
      });
    },
    [patchParams],
  );

  /**
   * Đổi NHIỀU tham số trong MỘT lần ghi URL. Gọi `updateParam` hai lần liền nhau thì lần sau dựng lại từ bộ tham số cũ và đè lần
   * trước (bấm "Đặt lại" kỳ của Phân tích chỉ xoá được ô "đến", còn ô "từ" ở lại).
   */
  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      patchParams((next) => {
        next.delete("page");
        for (const [key, value] of Object.entries(patch)) {
          if (value == null || value === "") next.delete(key);
          else next.set(key, value);
        }
      });
    },
    [patchParams],
  );

  /**
   * Column sort, in the URL like every other list control so a sorted view can
   * be bookmarked and shared.
   *
   * Three states, not two: ascending → descending → back to the list's own
   * default order. The old system needed a separate "Reset to newest first"
   * button because its sort was two-state and there was no way back; cycling
   * through off removes the need for that button entirely.
   */
  const sort = searchParams.get("sort");
  const dir: SortDir = searchParams.get("dir") === "asc" ? "asc" : "desc";
  const toggleSort = useCallback(
    (key: string) => {
      patchParams((next) => {
        next.delete("page");
        const current = next.get("sort");
        const currentDir = next.get("dir") === "asc" ? "asc" : "desc";
        if (current !== key) {
          next.set("sort", key);
          next.delete("dir"); // desc is the default and stays out of the URL
        } else if (currentDir === "desc") {
          next.set("dir", "asc");
        } else {
          next.delete("sort");
          next.delete("dir");
        }
      });
    },
    [patchParams],
  );

  const clampPage = useCallback(
    (total: number | undefined) => {
      if (total == null) return;
      const pageCount = Math.max(1, Math.ceil(total / pageSize));
      if (page <= pageCount) return;
      patchParams((next) => {
        if (pageCount === 1) next.delete("page");
        else next.set("page", String(pageCount));
      }, true);
    },
    [page, pageSize, patchParams],
  );

  return {
    q,
    page,
    pageSize,
    sort,
    dir,
    toggleSort,
    searchParams,
    updatePage,
    updatePageSize,
    updateSearch,
    /** Gắn thẳng vào ô tìm kiếm: `<Input {...list.searchInput} />` — xử lý đúng bộ gõ tiếng Hoa / Nhật / Hàn. */
    searchInput,
    updateParam,
    updateParams,
    clampPage,
  };
}

/**
 * Ô tìm kiếm gắn với URL mà gõ được tiếng Hoa (30/09/2026, Roary báo: gõ pinyin "heng" ra "hhehenheng", không chọn được chữ
 * Hán).
 *
 * Ô từng lấy giá trị thẳng từ URL: mỗi phím là một lần `setSearchParams`, giá trị quay về ô trễ một nhịp, nên bộ gõ tiếng Hoa
 * (và Nhật, Hàn, Telex của Windows) bị ghi chốt từng bước soạn chữ vào ô. Hook này giữ chữ ở state cục bộ — đúng thứ đang có
 * trong ô — và chỉ gọi `apply` (đẩy lên URL, tức gửi truy vấn) khi KHÔNG đang soạn chữ, sau 250 ms ngừng gõ. `applied` là giá
 * trị đang nằm trên URL; đổi từ nơi khác (quay lại trang trước, nút đặt lại…) thì ô theo, trừ khi đang soạn chữ.
 *
 * Dùng: `<Input {...useSearchDraft(q, apply)} />`.
 */
export function useSearchDraft(applied: string, apply: (value: string) => void) {
  const [draft, setDraft] = useState(applied);
  const composing = useRef(false);
  const pushed = useRef(applied);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Hàm của lần render mới nhất: `apply` đóng gói tham số URL hiện có, hàm cũ sẽ ghi đè bộ lọc vừa đổi trong 250 ms chờ.
  const applyRef = useRef(apply);
  useEffect(() => {
    applyRef.current = apply;
  }, [apply]);
  useEffect(() => {
    if (!composing.current && applied !== pushed.current) {
      pushed.current = applied;
      setDraft(applied);
    }
  }, [applied]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const schedule = (value: string) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (value === pushed.current) return;
      pushed.current = value;
      applyRef.current(value);
    }, 250);
  };
  return {
    value: draft,
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      setDraft(event.target.value);
      const native = event.nativeEvent as InputEvent;
      if (!composing.current && !native.isComposing) schedule(event.target.value);
    },
    onCompositionStart: () => {
      composing.current = true;
    },
    onCompositionEnd: (event: CompositionEvent<HTMLInputElement>) => {
      composing.current = false;
      const value = event.currentTarget.value;
      setDraft(value);
      schedule(value);
    },
  };
}

/** Ô lọc chữ tự do gắn với một tham số URL (người phụ trách, nhãn hiệu…) — cùng cách xử lý bộ gõ với ô tìm kiếm. */
export function useParamInput(
  list: Pick<ReturnType<typeof useListQueryState>, "searchParams" | "updateParam">,
  key: string,
  normalize: (value: string) => string = (value) => value,
) {
  const { updateParam } = list;
  return useSearchDraft(list.searchParams.get(key) ?? "", (value) => updateParam(key, normalize(value) || null));
}

/** Pull an out-of-range `page` back after a filter/search shrinks the result set. */
export function useClampListPage(
  total: number | undefined,
  clampPage: (total: number | undefined) => void,
) {
  useEffect(() => {
    clampPage(total);
  }, [total, clampPage]);
}

export function slicePage<T>(rows: readonly T[], page: number, pageSize: number): { rows: T[]; total: number } {
  const total = rows.length;
  const start = Math.max(0, (page - 1) * pageSize);
  return { total, rows: rows.slice(start, start + pageSize) };
}
